// NPSD data helpers — extracted from legacy-utils.ts

import { hasValue, hasStringValue } from './validation';
import { deepCopyProperties, indexOfValueInArrayByProperty } from './arrays';
import { getModuleType, getCurrentUserId, getCurrentCompanyId } from './auth';
import { isoDatetimeToFormattedDatetime } from './date-time';
import { canCurrentUserAccessCaseReport } from './permissions-helpers';
import { validateAddressWithSuggestions, hideLoadScreenIfParamTrue } from './custom-location';
import { App } from '@/services/app-compat';

const _w = window as any;
const DATETIME_FORMAT_SERVER_UTC = (window as any).DATETIME_FORMAT_SERVER_UTC;

export function setNPSDItemLocked(npsdId: string, isLocked: boolean, npsdType: string, callback: Function): void {
  if (hasStringValue(npsdId) && hasStringValue(npsdType) && hasStringValue(getModuleType())) {
    (window as any).api.lockNPSDItem(npsdId, isLocked, npsdType, () => {
      callback();
    });
  }
}

export function isNpsdDataEqual(npsdDataOriginal: any, npsdDataCurrent: any): boolean {
  if (!hasValue(npsdDataOriginal) && !hasValue(npsdDataCurrent)) {
    return true;
  }
  if (!hasValue(npsdDataOriginal) || !hasValue(npsdDataCurrent)) {
    return false;
  }

  const newNpsdDataOriginal = deepCopyProperties(npsdDataOriginal);
  const newNpsdDataCurrent = deepCopyProperties(npsdDataCurrent);

  if (hasValue(newNpsdDataCurrent) && hasValue(newNpsdDataCurrent.connections) && hasValue(newNpsdDataCurrent.connections.people) &&
    newNpsdDataCurrent.connections.people.length > 0) {
    for (const person of newNpsdDataCurrent.connections.people) {
      person.dob = isoDatetimeToFormattedDatetime(person.dob, DATETIME_FORMAT_SERVER_UTC);
    }
  }

  if (hasValue(newNpsdDataOriginal) && hasValue(newNpsdDataOriginal.connections) && hasValue(newNpsdDataOriginal.connections.people) &&
    newNpsdDataOriginal.connections.people.length > 0) {
    for (const person of newNpsdDataOriginal.connections.people) {
      person.dob = isoDatetimeToFormattedDatetime(person.dob, DATETIME_FORMAT_SERVER_UTC);
    }
  }

  const isPerson: boolean = npsdDataCurrent?.constructor?.name === "Person";
  if (isPerson) {
    newNpsdDataOriginal.height = null;
    newNpsdDataOriginal.weight = null;
    newNpsdDataOriginal.personWeight = null;

    newNpsdDataCurrent.height = null;
    newNpsdDataCurrent.weight = null;
    newNpsdDataCurrent.personWeight = null;
    delete newNpsdDataCurrent.personHeightMeters;
    delete newNpsdDataCurrent.personHeightFeet;
    delete newNpsdDataCurrent.personHeightInches;

    if (hasValue(newNpsdDataCurrent)) {
      if (hasValue(newNpsdDataCurrent.height) && newNpsdDataCurrent.height.mm === null) {
        newNpsdDataCurrent.height = null;
      }
      if (hasValue(newNpsdDataCurrent.weight) && newNpsdDataCurrent.weight.grams === null) {
        newNpsdDataCurrent.weight = null;
      }
    }

    return JSON.stringify(newNpsdDataOriginal) == JSON.stringify(newNpsdDataCurrent);
  }

  return JSON.stringify(newNpsdDataOriginal) === JSON.stringify(newNpsdDataCurrent);
}

export function npsdTypeToNpsdRelType(npsdType: string): any {
  if (!hasValue(npsdType)) {
    return null;
  }
  const npsdTypeLowerCase = npsdType.toLowerCase();
  if (npsdTypeLowerCase === 'person') {
    return _w.RelType.person;
  } else if (npsdTypeLowerCase === 'business') {
    return _w.RelType.business;
  } else if (npsdTypeLowerCase === 'vehicle') {
    return _w.RelType.vehicle;
  } else if (npsdTypeLowerCase === 'client' || npsdTypeLowerCase === 'clientgroup') {
    return _w.RelType.client;
  }
  return null;
}

export function gridNPSDDataReportCaseIdTemplate(e: any): string {
  const module: string = getModuleType();
  if (module === null || module === undefined) {
    return e.caseId;
  }
  if (window.location.href.indexOf("tab=CaseReports") > -1) {
    return e.caseId;
  }
  if (!canCurrentUserAccessCaseReport(e.author)) {
    return e.caseId;
  }
  return `<a href='/?sectionName=${module.toUpperCase()}&tab=CaseReports&function=displayCaseReport&multiModule=True&params=${e.id}'>${e.caseId}</a><a id='lnk'></a>`;
}

export function gridNPSDDataCallCardEventIdTemplate(e: any): string {
  const module: string = getModuleType();
  if (module === null || module === undefined) {
    return e.eventId;
  }
  if (window.location.href.indexOf("tab=CaseReports") > -1) {
    return e.eventId;
  } else {
    return `<a href='/?sectionName=${module.toUpperCase()}&tab=NewCallCard&function=CAD_CallCard&multiModule=True&params=${e.id}'>${e.eventId}</a><a id='lnk'></a>`;
  }
}

export function gridNPSDDataReportNatureOfCallValuesTemplate(e: any): string {
  return e.natureOfCallValues.join(", ");
}

export function gridNPSDDataReportViewTemplate(e: any): string {
  const module: string = getModuleType();
  if (module === null || module === undefined) {
    return "View Unavailable";
  }
  if (window.location.href.indexOf("tab=CaseReports") > -1) {
    return "";
  }
  if (!canCurrentUserAccessCaseReport(e.author)) {
    return "";
  }
  return `<a href='/?sectionName=${module.toUpperCase()}&tab=CaseReports&function=displayCaseReport&multiModule=True&params=${e.id}'>View</a><a id='lnk'></a>`;
}

export function saveAddNPSDData(params: any): void {
  const fillInAddressFromSuggestions = (window as any).fillInAddressFromSuggestions;
  const CaseRpt = (window as any).CaseRpt;
  const NPSDItemToReport = (window as any).NPSDItemToReport;

  const locationField: HTMLInputElement = params.locationField;
  const currentData = params.currentData;
  const npsdItemToReportId: string = params.npsdItemToReportId;
  const addressValidationErrorMsg = params.addressValidationErrorMsg;
  const npsdType: string = params.npsdType;
  const thisJQuery: any = params.thisJQuery;
  const verifyAddress: boolean = (params.verifyAddress === null || params.verifyAddress === undefined) ? true : false;
  const saveNPSDItem = () => {
    (window as any).api.saveNPSDItem(currentData, npsdType, (result: any) => {
      if (npsdType === "person") {
        App.originalPersonResult = null;
      } else if (npsdType === "vehicle") {
        App.originalVehicleResult = null;
      } else if (npsdType === "business") {
        App.originalBusinessResult = null;
      }

      NPSDItemToReport(npsdItemToReportId, result.id);
      if (CaseRpt.currentMDTRptController?.isSearchOpen()) {
        CaseRpt.currentMDTRptController.closeInvolvedSearchModal();
      }
    });
  };
  if (verifyAddress) {
    validateAddressWithSuggestions({
        errorMessage: addressValidationErrorMsg,
        isLocationRequired: false,
        locationField: locationField,
        companyId: getCurrentCompanyId(),
        // Previously-saved coords for discrepancy detection on edits.
        previousLat: currentData?.latitude ?? null,
        previousLng: currentData?.longitude ?? null,
      })
      .then((results: any) => {
        let dataLocationFields = null;
        if (npsdType === "person") {
          dataLocationFields = _w.DataObjectLocationFields.person;
        } else if (npsdType === 'business') {
          dataLocationFields = _w.DataObjectLocationFields.business;
        }

        fillInAddressFromSuggestions(results, currentData, locationField, dataLocationFields);
        saveNPSDItem();
      })
      .catch(() => {});
  } else {
    saveNPSDItem();
  }
}

export function getAttachmentInfoList(attachmentList: any[]): any[] {
  const attachmentInfoList: any[] = [];
  for (let i = 0; i < attachmentList.length; i++) {
    const attachment = new (_w.AttachmentInfo)();
    attachment.id = attachmentList[i].id;
    attachmentInfoList.push(attachment);
  }
  return attachmentInfoList;
}
